import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSurface } from "../hooks/useSurfaces";
import {
  useCrawlJob,
  useDiscoverPropertyContent,
  usePropertyContentCatalog,
  usePropertyContentItem,
  useRefreshPropertyContent,
  useReprocessPropertyPage,
} from "../hooks/usePropertyContentCatalog";
import { Badge } from "../components/ui/Badge";
import { ApiError } from "../lib/api";
import type { PropertyContentCatalogItem } from "../../../shared/crawl";

function formatTimestamp(value?: string | null): string {
  return value ? new Date(value).toLocaleString() : "Never";
}

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="space-y-6 p-4 sm:p-6">{children}</div>;
}

export function filterPropertyContentItems(items: PropertyContentCatalogItem[], query: string): PropertyContentCatalogItem[] {
  const value = query.trim().toLowerCase();
  if (!value) return items;
  return items.filter((item) => `${item.title ?? ""} ${item.source_url}`.toLowerCase().includes(value));
}

export function blueskyItemLabel(item: PropertyContentCatalogItem): "Profile" | "Post" | "Reply" {
  if (item.source_type === "social_bio") return "Profile";
  return item.provider_metadata?.item_kind === "reply" ? "Reply" : "Post";
}

function blueskyEmbedSummaries(item: PropertyContentCatalogItem): string[] {
  const embeds = item.provider_metadata?.embeds;
  if (!Array.isArray(embeds)) return [];
  return embeds.flatMap((embed) => {
    if (!embed || typeof embed !== "object" || !("kind" in embed) || typeof embed.kind !== "string") return [];
    const value = embed as Record<string, unknown>;
    const detail = typeof value.alt === "string" && value.alt.trim()
      ? value.alt
      : typeof value.title === "string" && value.title.trim()
        ? value.title
        : typeof value.status === "string" && value.status.trim()
          ? value.status
          : null;
    return [`${embed.kind[0]?.toUpperCase() ?? ""}${embed.kind.slice(1)}${detail ? ` — ${detail}` : ""}`];
  });
}

type BlueskyCatalogNotice = {
  tone: "info" | "warning" | "error";
  title: string;
  message: string;
};

export function getBlueskyCatalogNotice(input: {
  latestJobId: string | null;
  latestStatus: string | null;
  discoveryExtent?: string;
  failureReason?: string | null;
  mutationError?: { code?: string; status?: number; message: string } | null;
}): BlueskyCatalogNotice | null {
  const reason = input.failureReason ?? "";
  const mutation = input.mutationError;
  if (mutation?.code === "RATE_LIMITED" || mutation?.status === 429 || reason.includes("rate_limit")) {
    return { tone: "warning", title: "Profile processing is rate limited", message: "No new inventory was saved. Wait for the provider or account quota to recover, then try again." };
  }
  if (reason === "bluesky_identity_changed") {
    return { tone: "error", title: "Bluesky identity changed", message: "The profile no longer resolves to this property's saved DID. Reconcile the identity before processing again." };
  }
  if (mutation?.code === "UPSTREAM_ERROR" || reason === "provider_failure") {
    return {
      tone: "warning",
      title: "Bluesky is unavailable",
      message: input.latestStatus === "partial_success"
        ? "Bluesky became unavailable during processing. HelloConvo retained the profile records it had already read."
        : "The public profile could not be read. Previously saved content remains available.",
    };
  }
  if (mutation || input.latestStatus === "failed") {
    return { tone: "error", title: "Profile processing failed", message: mutation?.message ?? "The latest profile read failed. Previously saved content remains available." };
  }
  if (input.latestStatus === "partial_success" || input.discoveryExtent === "partial") {
    return { tone: "warning", title: "Partial profile inventory", message: "HelloConvo saved the records it could read. Coverage details below identify scope that was excluded or not reached." };
  }
  if (!input.latestJobId) {
    return { tone: "info", title: "Profile not processed yet", message: "Process this public profile to inventory its bio, posts, and replies." };
  }
  return null;
}

const NOTICE_STYLES: Record<BlueskyCatalogNotice["tone"], string> = {
  info: "border-brand-200 bg-brand-50 text-brand-800",
  warning: "border-amber-200 bg-amber-50 text-amber-900",
  error: "border-red-200 bg-red-50 text-red-800",
};

export function PropertyContentCatalogPage() {
  const { brandId = "", surfaceId = "" } = useParams();
  const surface = useSurface(brandId, surfaceId);
  const catalogQuery = usePropertyContentCatalog(brandId, surfaceId);
  const catalog = catalogQuery.data?.data;
  const canonicalUrl = surface.data?.data.canonical_url ?? "";
  const propertyKind = surface.data?.data.kind === "bluesky" ? "bluesky" : "website";
  const discover = useDiscoverPropertyContent(brandId, surfaceId, canonicalUrl, propertyKind);
  const refresh = useRefreshPropertyContent(brandId, surfaceId, catalog?.latest_job_id ?? null);
  const [query, setQuery] = useState("");
  const processing = discover.isPending || refresh.isPending || catalog?.latest_status === "queued" || catalog?.latest_status === "crawling";
  const items = useMemo(() => filterPropertyContentItems(catalog?.items ?? [], query), [catalog?.items, query]);

  if (surface.isLoading || catalogQuery.isLoading) return <PageShell><p className="text-sm text-slate-500">Loading website pages…</p></PageShell>;
  if (surface.isError || catalogQuery.isError) return <PageShell><p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Property content could not be loaded.</p></PageShell>;
  const property = surface.data?.data;
  const isWebsite = property?.kind === "website";
  const isBluesky = property?.kind === "bluesky" && property.status === "active";
  if (!property || (!isWebsite && !isBluesky)) return <PageShell><p className="text-sm text-red-700">Property not found.</p></PageShell>;

  const collectionLabel = isBluesky ? "Posts and profile" : "Pages";
  const searchLabel = isBluesky ? "Search posts and profile" : "Search pages";
  const hasProcessedProfile = (catalog?.items.length ?? 0) > 0
    || catalog?.latest_status === "done"
    || catalog?.latest_status === "partial_success";
  const providerScope = catalog?.inventory?.provider_scope as { actor_handle?: unknown } | undefined;
  const actorHandle = typeof providerScope?.actor_handle === "string" ? providerScope.actor_handle : null;
  const mutationError = discover.error ?? refresh.error;
  const blueskyNotice = isBluesky ? getBlueskyCatalogNotice({
    latestJobId: catalog?.latest_job_id ?? null,
    latestStatus: catalog?.latest_status ?? null,
    discoveryExtent: catalog?.inventory?.discovery_extent,
    failureReason: catalog?.inventory?.failure_reason,
    mutationError: mutationError instanceof Error ? {
      code: mutationError instanceof ApiError ? mutationError.code : undefined,
      status: mutationError instanceof ApiError ? mutationError.status : undefined,
      message: mutationError.message,
    } : null,
  }) : null;

  const processProperty = () => catalog?.latest_job_id ? refresh.mutate() : discover.mutate();
  return (
    <PageShell>
      <div>
        <Link to={`/brand/${brandId}/settings/surfaces`} className="text-sm text-brand-700 hover:underline">← Properties</Link>
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{isBluesky ? "Bluesky property" : "Website property"}</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">{property.display_name}</h1>
            <a href={canonicalUrl} className="mt-1 block text-sm text-brand-700 hover:underline" target="_blank" rel="noreferrer">{canonicalUrl}</a>
          </div>
          {isWebsite && (
            <button type="button" disabled={processing || !canonicalUrl} onClick={processProperty} className="w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 sm:w-auto">
              {processing ? "Processing website…" : "Process website"}
            </button>
          )}
          {isBluesky && (
            <button type="button" disabled={processing || !canonicalUrl} onClick={processProperty} className="w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 sm:w-auto">
              {processing ? "Processing profile…" : hasProcessedProfile ? "Refresh profile" : "Process profile"}
            </button>
          )}
        </div>
      </div>

      {isWebsite && mutationError && <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{mutationError.message}</p>}
      {isBluesky && blueskyNotice && (
        <div role={blueskyNotice.tone === "info" ? "status" : "alert"} className={`rounded-lg border px-4 py-3 text-sm ${NOTICE_STYLES[blueskyNotice.tone]}`}>
          <p className="font-medium">{blueskyNotice.title}</p>
          <p className="mt-1">{blueskyNotice.message}</p>
        </div>
      )}

      {isBluesky && (
        <dl className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
          <div><dt className="text-xs text-slate-500">Handle</dt><dd className="mt-1 text-sm font-medium text-slate-900">{actorHandle ? `@${actorHandle}` : "Not observed yet"}</dd></div>
          <div><dt className="text-xs text-slate-500">DID</dt><dd className="mt-1 break-all font-data text-xs text-slate-700">{property.external_id ?? "Unavailable"}</dd></div>
          <div><dt className="text-xs text-slate-500">Last checked</dt><dd className="mt-1 text-sm font-medium text-slate-900">{formatTimestamp(catalog?.inventory?.checked_at)}</dd></div>
          <div><dt className="text-xs text-slate-500">Known items</dt><dd className="mt-1 font-data text-sm font-medium text-slate-900">{catalog?.items.length ?? 0}</dd></div>
          <div><dt className="text-xs text-slate-500">Coverage</dt><dd className="mt-1 text-sm font-medium text-slate-900">{catalog?.inventory?.discovery_extent?.replaceAll("_", " ") ?? "Not checked"}</dd></div>
          <div><dt className="text-xs text-slate-500">Observed</dt><dd className="mt-1 font-data text-sm font-medium text-slate-900">{catalog?.inventory?.discovered_count ?? 0}</dd></div>
          <div><dt className="text-xs text-slate-500">Excluded</dt><dd className="mt-1 font-data text-sm font-medium text-slate-900">{catalog?.inventory?.excluded_count ?? 0}</dd></div>
          <div><dt className="text-xs text-slate-500">Not reached</dt><dd className="mt-1 font-data text-sm font-medium text-slate-900">{catalog?.inventory?.unattempted_count ?? 0}</dd></div>
        </dl>
      )}

      <section className="rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-4">
          <div>
            <h2 className="font-semibold text-slate-900">{collectionLabel}</h2>
            <p className="mt-1 text-xs text-slate-500">{catalog?.items.length ?? 0} known · last run {catalog?.latest_status?.replaceAll("_", " ") ?? "never"}</p>
          </div>
          <label className="w-full sm:w-72">
            <span className="sr-only">{searchLabel}</span>
            <input value={query} onInput={(event) => setQuery(event.currentTarget.value)} placeholder={searchLabel} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </label>
        </div>
        {items.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">{catalog?.items.length ? `No ${isBluesky ? "posts or profile details" : "pages"} match your search.` : `No ${isBluesky ? "posts or profile details" : "pages"} have been processed yet.`}</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {items.map((item) => {
              const publishedAt = typeof item.provider_metadata?.published_at === "string"
                ? item.provider_metadata.published_at
                : null;
              const itemLabel = isBluesky ? blueskyItemLabel(item) : null;
              return (
                <li key={item.id}>
                  <Link to={`./${item.id}`} className="flex flex-col items-start justify-between gap-4 px-4 py-4 hover:bg-slate-50 sm:flex-row sm:items-center">
                    <span className="min-w-0">{itemLabel && <Badge variant="info" className="mb-2">{itemLabel}</Badge>}<span className="block truncate text-sm font-medium text-slate-900">{item.title || item.source_url}</span><span className="mt-1 block truncate text-xs text-slate-500">{item.source_url}</span>{isBluesky && publishedAt && <span className="mt-1 block text-xs text-slate-500">Published {formatTimestamp(publishedAt)}</span>}</span>
                    <span className="shrink-0 text-xs text-slate-500">{item.capture_state === "captured" ? "Processed" : item.capture_state.replaceAll("_", " ")} →</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </PageShell>
  );
}

export function PropertyContentDetailPage() {
  const { brandId = "", surfaceId = "", itemId = "" } = useParams();
  const navigate = useNavigate();
  const surface = useSurface(brandId, surfaceId);
  const itemQuery = usePropertyContentItem(brandId, surfaceId, itemId);
  const item = itemQuery.data?.data;
  const reprocess = useReprocessPropertyPage(brandId, surfaceId, item?.source_url ?? "");
  const [jobId, setJobId] = useState<string | null>(null);
  const job = useCrawlJob(brandId, jobId);
  const jobStatus = job.data?.data.status;

  useEffect(() => {
    if (jobStatus === "done" || jobStatus === "partial_success" || jobStatus === "failed") void itemQuery.refetch();
  }, [jobStatus]);

  if (surface.isLoading || itemQuery.isLoading) return <PageShell><p className="text-sm text-slate-500">Loading page…</p></PageShell>;
  if (surface.isError || itemQuery.isError) return <PageShell><p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">Property record could not be loaded.</p></PageShell>;
  if (!item) return <PageShell><p className="text-sm text-red-700">Page not found.</p></PageShell>;
  const isBluesky = surface.data?.data.kind === "bluesky";
  const itemLabel = isBluesky ? blueskyItemLabel(item) : "Website page";
  const publishedAt = typeof item.provider_metadata?.published_at === "string" ? item.provider_metadata.published_at : null;
  const embedSummaries = isBluesky ? blueskyEmbedSummaries(item) : [];
  const isNonText = isBluesky && item.content_state === "non_text";
  const busy = reprocess.isPending || jobStatus === "queued" || jobStatus === "crawling";

  return (
    <PageShell>
      <div>
        <button type="button" onClick={() => navigate(`/brand/${brandId}/settings/surfaces/${surfaceId}/content`)} className="text-sm text-brand-700 hover:underline">← {isBluesky ? "All posts and profile" : "All pages"}</button>
        <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0"><p className="text-xs font-medium uppercase tracking-wide text-slate-500">{itemLabel}</p><h1 className="mt-1 text-2xl font-semibold text-slate-900">{item.title || (isBluesky ? itemLabel : "Untitled page")}</h1><a href={item.source_url} target="_blank" rel="noreferrer" className="mt-1 block break-all text-sm text-brand-700 hover:underline">{item.source_url}</a></div>
          {!isBluesky && <button type="button" disabled={busy} onClick={() => reprocess.mutate(undefined, { onSuccess: (response) => setJobId(response.data.id) })} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">{busy ? "Reprocessing page…" : "Reprocess this page"}</button>}
        </div>
      </div>
      {(reprocess.error || jobStatus === "failed") && <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{reprocess.error?.message ?? job.data?.data.error ?? "Page processing failed. The prior saved document is still available."}</p>}
      {item.failure_reason && !reprocess.error && jobStatus !== "failed" && (
        <p role="alert" className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {isBluesky ? "The latest profile read could not refresh this record." : `The latest website run could not process this page (${item.failure_reason}).`} {isNonText ? "The catalog metadata below is the last observed version." : "The saved text below is the last successful version."}
        </p>
      )}
      <dl className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-3">
        <div><dt className="text-xs text-slate-500">Last checked</dt><dd className="mt-1 text-sm font-medium text-slate-900">{formatTimestamp(item.last_checked_at)}</dd></div>
        <div><dt className="text-xs text-slate-500">Last processed</dt><dd className="mt-1 text-sm font-medium text-slate-900">{formatTimestamp(item.last_successful_capture_at)}</dd></div>
        <div><dt className="text-xs text-slate-500">Current revision</dt><dd className="mt-1 text-sm font-medium text-slate-900">{item.revision_count || "—"}</dd></div>
        {isBluesky && <div><dt className="text-xs text-slate-500">Published</dt><dd className="mt-1 text-sm font-medium text-slate-900">{formatTimestamp(publishedAt)}</dd></div>}
      </dl>
      {isNonText ? (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="font-semibold text-slate-900">Non-text record</h2>
          <p className="mt-2 text-sm text-slate-600">This Bluesky record has no authored text document. Its source and bounded media metadata remain available.</p>
          {embedSummaries.length > 0 && <ul className="mt-4 space-y-2 text-sm text-slate-700">{embedSummaries.map((summary, index) => <li key={`${summary}-${index}`} className="rounded-lg bg-slate-50 px-3 py-2">{summary}</li>)}</ul>}
        </section>
      ) : (
        <section className="rounded-xl border border-slate-200 bg-white p-5"><h2 className="font-semibold text-slate-900">{isBluesky ? "Saved text" : "Saved document"}</h2><pre className="mt-4 whitespace-pre-wrap font-sans text-sm leading-6 text-slate-700">{item.current_document_content || (isBluesky ? "No saved text is available for this record." : "No saved document is available for this page.")}</pre></section>
      )}
    </PageShell>
  );
}
